import { Color } from '@lux/wardrobe-lib'
import { SmartlyActionItems } from '@Techedge/digitalportal-shared'

export enum ProjectApiQuestionType {
    TEXT = 'text',
    SINGLE_CHOICE = 'singleChoice',
    MULTI_CHOICE = 'multiChoice',
    NUMERIC = 'numeric',
    YESNO = 'yesNo',
    DATE = 'date',
}

export enum ProjectApiStatus {
    DRAFT = 'draft',
    TO_BE_RELEASED = 'toBeReleased',
    NEW = 'new',
    READ = 'read',
    REJECTED = 'rejected',
}

export enum ProjectApiVisibility {
    IMMEDIATE = 'immediate',
    OVERNIGHT = 'overnight',
    DAYS_BEFORE_START_DATE = 'daysBeforeStartDate',
    SPECIFIC_DATE = 'specificDate',
}
export interface ProjectApiQuestion {
    _id?: string
    questionTitle: string //testo della domanda

    possibleAnswers: string[] //lista delle possibili risposte

    questionType: ProjectApiQuestionType // tipo di domanda. I valori possibili sono text, singleChoice, multiChoice, numeric, yesNo, date

    attachmentsRequired: boolean // indica che per rispondere alla domanda è necessario anche caricare un allegato
    lastupdate: number
}

export interface ProjectApiJob {
    _id?: string
    title: string //titolo del job

    details: string //descrizione del job

    questions: ProjectApiQuestion[] //lista delle domande associate al job (la struttura è la stessa del blocco precedente)

    priority: string[] // priorità del job. I valori possibili sono standard e urgent

    startDate: number //data di inizio esecuzione del job. Il formato della data è un long (e.g. 1670761800000)

    endDate: number // data di fine esecuzione del job. Il formato della data è un long (e.g. 1670761800000)

    assignment: string // assegnatario del job. I valori possibili sono regionalmanager, storemanager e storeassociate
}

export interface ProjectApiSurvey {
    _id?: string
    title: string // titolo del blocco survey

    details: string //descrizione del blocco survey

    priority: string // priorità del blocco survey

    startDate: number // data di inizio esecuzione della survey. Il formato della data è un long (e.g. 1670761800000)

    endDate: number // data di fine esecuzione della survey. Il formato della data è un long (e.g. 1670761800000)

    recipient: string //ruolo esecutore della survey. I valori possibili sono regionalmanager, storemanager e storeassociate

    surveyId: string // identificativo univoco della survey
    surveyName: string
    surveyUrl: string
    answer: { surveyResponse: { responseId: string } }
    surveyTaskList: { id: string; projectTitle: string; status: string }[]
    assignmentUserId: string[]
    assignment: string[]
}

export interface Attachment {
    _id?: string
    id?: string
    url?: string
    name?: string
    ext?: string
}

interface IRecursiveTime {
    timeInterval: number
}
interface IRecursiveWeeklyTime extends IRecursiveTime {
    day?: number
}

export interface ActionUserInfo {
    name: string
    lastname: string
    nickname?: string
    role: string
    userId: string
    date: number
    macroreason?: string
    reason?: string
    message?: string | { questions?: string; surveys?: string; jobs?: string; photos?: string }
}
export interface ProjectApiCreateActivityBody {
    _id?: string
    coneOfVision?: any[]

    attachments?: Attachment[]
    projectTitle: string

    department?: string // id del dipartimento da associare al progetto. La lista dei dipartimenti è restituita da un servizio del blocco filters

    assignment: string[] // lista di ruoli assegnatari; per i progetti del communication l’array dovrà avere un solo elemento selezionabile tra regionalmanager, storemanager e storeassociate
    assignmentUserIds?: string[] // lista degli utent delegati

    creatorUserInfo?: ActionUserInfo

    enableMessaging: boolean // flag per abilitare lo scambio di messaggi sul progetto
    enableCommentsAndReactions: boolean
    priority: string //priorità del progetto. I valori possibili sono standard e urgent

    status: ProjectApiStatus // utilizzato per differenziare il salvataggio in draft (valorizzare il campo con draft) dal salvataggio effettivo (valorizzare il campo con toBeReleased)

    projectType: SmartlyActionItems // tipo di progetto. I valori possibili sono task, news e visit

    module: string // modulo al quale appartiene il progetto. I valori possibili sono communication, svt e pm
    type: string // subtype
    childrenSubType?: string

    requiredConfirmReading: boolean // flag per indicare che per l’esecuzione del task è necessario aver confermato la lettura

    information: string // campo contenente la descrizione del progetto

    numberOfPhotos?: number // numero minimo di foto per task fotografici

    questions?: ProjectApiQuestion[] // lista delle domande alle quali l’utente esecutore deve rispondere per eseguire il progetto. Ogni question contiene i seguenti campi:

    jobs?: ProjectApiJob[] //lista dei job che l’utente esecutore deve svolgere per eseguire il progetto. Ogni job contiene i seguenti campi:

    approverRequired?: boolean
    approvers?: string[]
    storesIdList: string[] //lista degli storeId associati al progetto, in formato stringa
    storesList?: any[]
    startDate: number //data di inizio progetto. Il formato della data è un long (e.g. 1670761800000)

    endDate: number // data di fine progetto. Il formato della data è un long (e.g. 1670761800000)

    visibility: ProjectApiVisibility // informazione sulla visibilità del progetto. Può avere i seguenti valori:
    visibilityDate?: number //data in cui il progetto sarà visibile in caso di visibility = specificDate

    autoclose: boolean //se true il progetto si chiuderà automaticamente alla endDate
    autocloseDate?: any
    visibilityCalendar: boolean // se true il progetto sarà visibile anche nella sezione del calendario

    surveys?: ProjectApiSurvey[] // lista delle survey che l’utente esecutore deve compilare per eseguire il progetto. Ogni survey contiene i seguenti campi:
    surveysSvt?: ProjectApiSurvey[]
    timeZone: string // timezone dell’utente che ha creato il progetto (e.g. UTC)

    smartFilter?: any

    rejectionUserInfo?: ActionUserInfo
    cancelledUserInfo?: ActionUserInfo
    redoUserInfo?: ActionUserInfo
    closerUserInfo?: ActionUserInfo
    cover?: Attachment
    reasonChoice?: string[]
    reasonChange?: string
    messageStores?: string
    isRecursive?: boolean
    recursiveInfo?: {
        daily?: IRecursiveTime
        weekly?: IRecursiveWeeklyTime
        monthly?: IRecursiveTime
        annual?: IRecursiveTime
        /**  recursive from */
        recursiveDateTimeFrom: number
        /** recursive end*/
        recursiveDateTimeTo?: number
        endOccurrences?: number
        noDeadline?: boolean
    }
    parentSurveyId?: string
    parentId?: string
    parentQualtricsQuestionId?: string
    remoteVisit?: boolean

    badgeId?: string;
    badgeType?: string;
    badgeActivityPoints?: number;
}

export interface TaskPhotoUpload {
    projectId?: string
    storeId?: string
    attachment: {
        id: string
    }
    macroarea: string
    fixtureCount: number
}

export const statusColors = {
    draft: Color.GREY2,
    new: Color.VIOLET,
    read: Color.BLUE,
    toBeReleased: Color.WARNING,
    released: Color.LIGHT_BLUE,
    redo: Color.YELLOW,
    inProgress: Color.WARNING,
    rejected: Color.ERROR,
    inApproval: Color.TEAL,
    rework: Color.YELLOW,
    overdue: Color.PINK,
    cancelled: Color.ERROR,
    planned: Color.LIGHT_BLUE,
    closed: Color.GREEN,
    saved: Color.LIGHT_BLUE,
    approvedWithWarning: Color.BROWN,
    approved: Color.GREEN,
    publishedWithWarning: Color.TEAL,
    published: Color.PURPLE,
    recalled: Color.RED,
}
